import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  ShieldCheck,
  Upload,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Ban,
  Camera,
  ScanFace,
  Loader2,
} from "lucide-react";
import { useAuth } from "../App";
import IDDocumentsPanel from "../components/IDDocumentsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
const ACCEPT = "image/jpeg,image/png,image/webp";

// ID Vault — self-serve KYC upload. A regular selfie (clear photo of your
// face — no dated paper) plus optional government ID. Uploads go to Emergent
// Object Store (encrypted at rest), never to Mongo. A GPT-5 vision scan runs
// in the background and writes a signed verdict row to id_vault_scans.
const IDVault = () => {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [idFront, setIdFront] = useState(null);
  const [idBack, setIdBack] = useState(null);
  const [selfie, setSelfie] = useState(null);

  const fetchStatus = useCallback(async () => {
    if (!token) return;
    try {
      // Universal /me endpoint — works for both fans AND artists.
      const { data } = await axios.get(`${API}/me/id-vault/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus({
        status: data.kyc_status,   // normalise to the same field the old artist API used
        uploaded_at: data.uploaded_at,
        scan_verdict: data.scan_verdict,
        scan_at: data.scan_at,
        last_verification: data.verification_action ? { action: data.verification_action, reason: data.reason } : null,
        verified_for_payouts: data.kyc_status === "approved",
        gate_open: data.gate_open,
        reason: data.reason,
        documents_redacted: data.documents_redacted,
        redacted_at: data.redacted_at,
        can_redact: data.can_redact,
        redact_blockers: data.redact_blockers || [],
        id_hold_days: data.id_hold_days || 30,
      });
      // Refresh the auth context so ProtectedRoute lifts the sentry as
      // soon as the AI/admin approves the ID.
      if (typeof refreshUser === "function" && data.kyc_status === "approved") {
        refreshUser();
      }
    } catch (e) {
      if (e.response?.status !== 403) toast.error("Failed to load KYC status");
    } finally {
      setLoading(false);
    }
  }, [token, refreshUser]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // While the scan is in-flight, poll every 5s so an AI auto-approval
  // instantly flips the gate open without the user having to refresh.
  useEffect(() => {
    if (!status || status.status === "approved" || status.status === "denied") return;
    if (status.status !== "scanning" && status.status !== "under_review") return;
    const t = setInterval(fetchStatus, 5000);
    return () => clearInterval(t);
  }, [status, fetchStatus]);

  const pick = (setter) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_UPLOAD_BYTES) {
      toast.error(`File too large — 5 MB max (got ${(f.size / 1024 / 1024).toFixed(1)} MB)`);
      e.target.value = "";
      return;
    }
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Only JPEG, PNG, or WEBP images accepted");
      e.target.value = "";
      return;
    }
    setter(f);
  };

  const submit = async () => {
    if (!idFront) {
      toast.error("The front of your government-issued photo ID is required");
      return;
    }
    if (!selfie) {
      toast.error("A selfie is required to verify your identity");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    // Government ID (front) + selfie are both required; back is optional.
    fd.append("id_front", idFront);
    if (idBack) fd.append("id_back", idBack);
    fd.append("selfie", selfie);
    try {
      await axios.post(`${API}/me/id-vault/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Uploaded — AI face-match + ID scan running now. Check back in ~30 seconds.");
      setIdFront(null); setIdBack(null); setSelfie(null);
      // Poll once quickly, then again after a delay
      await new Promise((r) => setTimeout(r, 2500));
      fetchStatus();
      setTimeout(fetchStatus, 15000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const statusLabel = {
    not_started: { label: "Not started", tone: "grey", icon: Upload },
    not_submitted: { label: "Not submitted", tone: "grey", icon: Upload },
    scanning: { label: "AI scan running", tone: "amber", icon: Loader2 },
    under_review: { label: "Awaiting admin review", tone: "amber", icon: Clock },
    approved: { label: "Approved", tone: "green", icon: CheckCircle2 },
    denied: { label: "Denied — see reason below", tone: "red", icon: Ban },
    on_hold: { label: "On hold — see reason below", tone: "amber", icon: AlertTriangle },
  };
  const s = status?.status ? (statusLabel[status.status] || statusLabel.not_started) : statusLabel.not_started;
  const StatusIcon = s.icon;

  if (!user) return null;

  return (
    <div className="min-h-screen bg-black text-white pb-24" data-testid="id-vault-page">
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur border-b border-[#1A2030]">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-lg border border-[#1A2030] hover:bg-[#1A2030] flex items-center justify-center"
            data-testid="idvault-back-btn"
            aria-label="Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Link to="/home" className="flex items-center gap-2">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
            <span className="hidden sm:inline text-white font-bold">StardomFans · Compliance</span>
          </Link>
          <div className="ml-auto text-right">
            <p className="text-[10px] uppercase tracking-widest text-[#8A92A0]">Compliance</p>
            <p className="text-white font-bold">ID Vault</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Status card */}
        <section className="rounded-xl border border-[#1A2030] bg-[#0A0E18] p-5" data-testid="idvault-status">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
              s.tone === "green" ? "bg-[#8DB87A]/10 border border-[#8DB87A]/30" :
              s.tone === "red"   ? "bg-[#EC008C]/10 border border-[#EC008C]/30" :
              s.tone === "amber" ? "bg-[#F0C464]/10 border border-[#F0C464]/30" :
                                    "bg-[#1A2030] border border-[#1A2030]"
            }`}>
              <StatusIcon className={`w-5 h-5 ${
                s.tone === "green" ? "text-[#8DB87A]" :
                s.tone === "red"   ? "text-[#EC008C]" :
                s.tone === "amber" ? "text-[#F0C464]" :
                                      "text-[#8A92A0]"
              } ${status?.status === "scanning" ? "animate-spin" : ""}`} />
            </div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">Verification status</p>
              <h1 className="text-2xl font-bold text-white" data-testid="idvault-status-label">{s.label}</h1>
              {status?.scan_verdict && (
                <div className="mt-2 text-xs text-[#8A92A0] space-y-1">
                  <p>AI-Selfie deepfake confidence: <span className="text-white font-bold">{status.scan_verdict.deepfake_confidence}%</span> {status.scan_verdict.deepfake_confidence >= 50 ? "(elevated)" : "(low)"}</p>
                  <p>Faces match ID: <span className="text-white font-bold">{status.scan_verdict.faces_match ? "yes" : "no"}</span></p>
                </div>
              )}
              {status?.last_verification?.reason && (
                <p className="text-xs text-[#F0C464] mt-2" data-testid="idvault-verif-reason">
                  <strong>Admin note:</strong> {status.last_verification.reason}
                </p>
              )}
              {status?.status === "approved" && (
                <button
                  onClick={() => navigate(user?.role === "corporate" ? "/corporate-dashboard" : "/dashboard")}
                  className="mt-3 inline-flex items-center gap-2 h-9 px-4 rounded-full bg-[#EC008C] text-black text-sm font-bold hover:bg-[#FF3399]"
                  data-testid="idvault-goto-dashboard-btn"
                >
                  Continue to your dashboard →
                </button>
              )}
              {status?.status && status.status !== "approved" && status.status !== "not_started" && (
                <p className="text-xs text-[#8A92A0] mt-2">
                  This page refreshes automatically while our AI runs the deepfake scan. Please hold on ~30 seconds after uploading.
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Instructions */}
        <section className="rounded-xl border border-[#1A2030] bg-[#0A0E18] p-5">
          <h2 className="font-bold text-white text-lg mb-3">What we need</h2>
          <ul className="space-y-2 text-sm text-[#8A92A0]">
            <li>• A <strong className="text-white">government-issued photo ID</strong> — passport, driver&apos;s license or provincial/state ID. Front is required; add the back if it carries information.</li>
            <li>• A <strong className="text-white">clear selfie</strong> — face the camera in good light, no sunglasses, hat or filters. You do <strong className="text-white">not</strong> need to hold up a date or a piece of paper.</li>
            <li>• 5 MB max per file · JPEG / PNG / WEBP only · <strong className="text-white">use the original photo</strong> — do not screenshot or filter</li>
            <li>• Our AI checks that the face on your ID matches your selfie, that the ID is authentic, and that neither image is AI-generated. <span className="text-[#EC008C] font-semibold">Most people are approved in under a minute.</span></li>
          </ul>
          <p className="mt-3 text-[11px] text-[#F0C464]">
            <ShieldCheck className="w-3 h-3 inline-block mr-1 -mt-0.5" />
            Files are encrypted at rest in our compliance vault. Only the verification admin can view them, and every access is logged in an append-only audit trail.
          </p>
        </section>

        {/* Upload form */}
        <section className="rounded-xl border border-[#1A2030] bg-[#0A0E18] p-5 space-y-4">
          <UploadRow label="Government ID — front" testid="id-front" file={idFront} onChange={pick(setIdFront)} required />
          <UploadRow label="Government ID — back (if applicable)" testid="id-back" file={idBack} onChange={pick(setIdBack)} />
          <UploadRow label="Selfie (clear photo of your face)" testid="selfie" file={selfie} onChange={pick(setSelfie)} required icon={Camera} />

          <button
            onClick={submit}
            disabled={uploading || !selfie || !idFront}
            className="w-full h-11 rounded-lg bg-[#EC008C] text-black font-bold text-sm inline-flex items-center justify-center gap-2 hover:bg-[#FF3399] disabled:opacity-60"
            data-testid="idvault-submit-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanFace className="w-4 h-4" />}
            {uploading ? "Uploading + scanning..." : "Submit for verification"}
          </button>
        </section>

        {/* Self-service ID document deletion */}
        {status?.uploaded_at && (
          <IDDocumentsPanel status={status} token={token} onChanged={fetchStatus} />
        )}
      </main>
    </div>
  );
};

const UploadRow = ({ label, testid, file, onChange, required, icon: Icon }) => (
  <div>
    <label className="text-xs text-[#8A92A0] mb-1 block">
      {label} {required && <span className="text-[#EC008C]">*</span>}
    </label>
    <label className="flex items-center gap-3 h-14 px-3 rounded-lg border border-dashed border-[#1A2030] hover:border-[#EC008C]/50 cursor-pointer transition">
      {Icon ? <Icon className="w-5 h-5 text-[#EC008C]" /> : <Upload className="w-5 h-5 text-[#8A92A0]" />}
      <div className="flex-1">
        {file ? (
          <>
            <p className="text-sm text-white font-medium truncate" data-testid={`${testid}-filename`}>{file.name}</p>
            <p className="text-[10px] text-[#8A92A0]">{(file.size / 1024).toFixed(0)} KB · {file.type}</p>
          </>
        ) : (
          <p className="text-sm text-[#8A92A0]">Click to select · 5 MB max</p>
        )}
      </div>
      <input type="file" accept={ACCEPT} className="hidden" onChange={onChange} data-testid={`${testid}-input`} />
    </label>
  </div>
);

export default IDVault;
